import { isInMarketNJ } from '../utils/isInMarketLocation';

export { isInMarketNJ };

export const BODY_SORT_CRITERIA = 'relevancy';

/**
 * Returns a Coveo preprocessRequest callback with its dynamic dependencies injected as
 * getter functions so the callback reads current values at request time (not at
 * engine-build time).
 *
 * The active scenario key is derived and stored in currentScenarioKeyRef before every
 * executeSearch dispatch; this callback reads it via getScenarioKey() at actual request
 * time — no closure staleness risk.
 *
 * Early-exit behaviour: for non-search requests (method !== 'search') the request is
 * returned unmodified — no rankingFunctions, queryFunctions, sortCriteria, or aq
 * modifications are written (AC-08).
 *
 * Step 5a (AHSMKTG-4136 Fix 2): body.context.rankingMode is derived from scenarioKey on
 * every search request, preserving any existing body.context keys (AC-21, AHSMKTG-4178).
 * Deriving from scenarioKey (already computed in step 3) keeps context.rankingMode in sync
 * with the actual boost ruleset being applied — correct on interfaceLoad in SSR environments
 * where getRankingMode() would have read a stale selectedSort.current === null.
 * On the outside-matches search only (getIsOutsideMatches()), step 5a also writes
 * body.context.outsideMatches = 'true' (AHSMKTG-4185); the key is omitted otherwise.
 *
 * VPC AQ steps (AHSMKTG-4019):
 *   Step 6 — VPC inclusion OR-clause: fires only when isInMarketNJ(geo) &&
 *             !getIsVpcOutsideMatches() && getIsVpcQualifyingSearch() && body.aq.
 *             Composition guard (!getIsVpcOutsideMatches) prevents co-firing with step 7.
 *             isVpcQualifyingSearch gate enforces BRs (qualifying terms/specialties only).
 *             body.aq guard (v2.1.0): the clause extends an existing geographic filter
 *             only — never fires standalone when there is no AQ to extend (AC-22).
 *   Step 7 — getIsVpcOutsideMatches: appends the VPC exclusion AND NOT clause when
 *             isVpcOutsideMatches is true (Scenario 3 outside-matches search).
 *
 * @param {object}                  getters
 * @param {() => object|null}       getters.getGeolocation           - Current geolocation { lat, lng, city } | null
 * @param {() => string}            getters.getScenarioKey           - Active FAD_BOOST_* scenario key
 * @param {() => object}            getters.getBoostRulesets         - Full boostRulesets map
 * @param {() => boolean}           [getters.getIsVpcOutsideMatches]  - Whether isVpcOutsideMatches is true;
 *                                                                       defaults to () => false when omitted
 * @param {() => boolean}           [getters.getIsVpcQualifyingSearch] - Whether current search qualifies for
 *                                                                        VPC inclusion per BRs (term/specialty
 *                                                                        check); defaults to () => true when
 *                                                                        omitted (backward-compat only)
 * @param {() => boolean}           [getters.getIsOutsideMatches]      - Whether the current search is an
 *                                                                        outside-matches pass; defaults to
 *                                                                        () => false when omitted
 * @param {() => boolean}           [getters.getHasLocation]            - Whether the current search URL has
 *                                                                        a zip-code or city param; defaults
 *                                                                        to () => true for backward-compat.
 *                                                                        When false, the VPC OR-clause in
 *                                                                        step 6 is suppressed regardless of
 *                                                                        what getGeolocation() returns
 *                                                                        (TC-7.1 interfaceLoad fix).
 * @returns {(request, clientOrigin, metadata) => object}
 */
export function buildPreprocessRequest({
    getGeolocation,
    getScenarioKey,
    getBoostRulesets,
    getIsVpcOutsideMatches = () => false,
    getIsVpcQualifyingSearch = () => true,
    getIsOutsideMatches = () => false,
    getHasLocation = () => true,
}) {
    return (request, _clientOrigin, metadata) => {
        if (!metadata || metadata.method !== 'search') {
            return request;
        }

        const body = JSON.parse(request.body);

        // Step 3: inject rankingFunctions from the pre-assembled boostRulesets config.
        // Falls back to [] when the key is absent (AC-13).
        // Note: entries with isConstant (QREs) are intentionally excluded — client-sent
        // rankingExpressions are discarded by the pipeline's PreprocessQuery step, so
        // VPC deboost is handled as a pipeline-configured QRE in the Coveo Admin Console.
        const scenarioKey = getScenarioKey();
        const boostRulesets = getBoostRulesets();
        const allEntries = (boostRulesets && boostRulesets[scenarioKey]) ?? [];
        body.rankingFunctions = allEntries.filter((e) => !('isConstant' in e));

        // Step 4: inject queryFunctions when geolocation is resolved.
        const geolocation = getGeolocation();
        if (geolocation != null) {
            body.queryFunctions = [
                {
                    fieldName: '@mydistance',
                    function: `min(dist(@loc1latitude,@loc1longitude,${geolocation.lat ?? 0},${
                        geolocation.lng ?? 0
                    }),dist(@loc2latitude,@loc2longitude,${geolocation.lat ?? 0},${
                        geolocation.lng ?? 0
                    }))`,
                },
                {
                    fieldName: '@mydistanceinmiles',
                    function: '@mydistance*0.000621371',
                },
            ];
        }

        // Step 5: always override sortCriteria to 'relevancy' (R-01 invariant).
        body.sortCriteria = BODY_SORT_CRITERIA;

        // Step 5a (AHSMKTG-4136 Fix 2 / AHSMKTG-4178): derive rankingMode from scenarioKey
        // (already computed in step 3) — the single source of truth for what sort mode and
        // boost ruleset are actually active. Preserves any existing context keys.
        // AHSMKTG-4185: outsideMatches is sent as the string 'true' (Coveo context values are
        // string | string[]) and ONLY on the "Matches Outside Your Search Area" search, so a
        // pipeline rule can target that section. The key is omitted on every other search.
        body.context = {
            ...(body.context ?? {}),
            rankingMode: scenarioKey.includes('NEXT_AVAILABLE') ? 'nextAvailable' : 'relevancy',
            ...(getIsOutsideMatches() ? { outsideMatches: 'true' } : {}),
        };

        // Step 6 (AHSMKTG-4019): VPC AQ inclusion clause.
        // Composition guard: !getIsVpcOutsideMatches() prevents co-firing with step 7.
        // isVpcQualifyingSearch gate: fires only for qualifying terms/specialties (BRs).
        // body.aq guard (v2.1.0): the VPC clause extends an existing geographic filter
        // only — it must never fire standalone when there is no AQ to extend (AC-22).
        const vpcClause =
            '(@specialtyservices == "Virtual Care" AND @onlinebookingavailable == True)';
        if (
            getHasLocation() &&
            isInMarketNJ(geolocation) &&
            !getIsVpcOutsideMatches() &&
            getIsVpcQualifyingSearch() &&
            body.aq
        ) {
            body.aq = `${body.aq} OR ${vpcClause}`;
        }

        // Step 7 (AHSMKTG-4019): VPC exclusion filter — two branches (AC-10).
        // (a) getIsVpcOutsideMatches(): Scenario 3 NJ all-VPC outside-matches.
        //     Composition guard suppressed step 6, so body.aq is typically empty.
        // (b) getIsOutsideMatches() && !isInMarketNJ(geo): Scenario 4 non-NJ outside-matches.
        //     Radius is removed for the outside-matches pass; lat/lng=0,0 no longer excludes
        //     VPCs. NOT clause required to satisfy AC-10 "no VPCs in results."
        // Null base-case guard applies to both branches — AND prefix requires a left operand.
        if (getIsVpcOutsideMatches() || (getIsOutsideMatches() && !isInMarketNJ(geolocation))) {
            const notClause = 'NOT @specialtyservices == "Virtual Care"';
            body.aq = body.aq?.trim() ? `${body.aq} AND ${notClause}` : notClause;
        }

        request.body = JSON.stringify(body);
        return request;
    };
}
